'use client';

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

type AccentColor = '#4A90D9' | '#4DBFBF' | '#7BC67E' | '#B5CC4A' | '#E8964D' | '#E85D5D' | '#D45CAC';
type ThemeMode = 'dark' | 'light';

interface ThemeContextValue {
  mode: ThemeMode;
  accent: AccentColor;
  toggleMode: () => void;
  setAccent: (color: AccentColor) => void;
}

const ThemeContext = createContext<ThemeContextValue>({
  mode: 'dark',
  accent: '#4A90D9',
  toggleMode: () => {},
  setAccent: () => {},
});

export const ACCENT_COLORS: { name: string; value: AccentColor }[] = [
  { name: 'Blue', value: '#4A90D9' },
  { name: 'Teal', value: '#4DBFBF' },
  { name: 'Green', value: '#7BC67E' },
  { name: 'Yellow-Green', value: '#B5CC4A' },
  { name: 'Orange', value: '#E8964D' },
  { name: 'Coral-Red', value: '#E85D5D' },
  { name: 'Magenta-Pink', value: '#D45CAC' },
];

const VALID_ACCENTS: AccentColor[] = ['#4A90D9', '#4DBFBF', '#7BC67E', '#B5CC4A', '#E8964D', '#E85D5D', '#D45CAC'];

function getInitialMode(): ThemeMode {
  if (typeof window === 'undefined') return 'dark';
  return (localStorage.getItem('prizma-theme') as ThemeMode) || 'dark';
}

function getInitialAccent(): AccentColor {
  if (typeof window === 'undefined') return '#4A90D9';
  const saved = localStorage.getItem('prizma-accent') as AccentColor | null;
  return saved && VALID_ACCENTS.includes(saved) ? saved : '#4A90D9';
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [mode, setMode] = useState<ThemeMode>(getInitialMode);
  const [accent, setAccentState] = useState<AccentColor>(getInitialAccent);

  useEffect(() => {
    const root = document.documentElement;
    if (mode === 'dark') {
      root.classList.add('dark');
      root.classList.remove('light');
    } else {
      root.classList.add('light');
      root.classList.remove('dark');
    }
    localStorage.setItem('prizma-theme', mode);
  }, [mode]);

  useEffect(() => {
    document.documentElement.style.setProperty('--accent', accent);
    document.documentElement.style.setProperty('--primary', accent);
    document.documentElement.style.setProperty('--ring', accent);
    localStorage.setItem('prizma-accent', accent);
  }, [accent]);

  const toggleMode = useCallback(() => {
    setMode(prev => (prev === 'dark' ? 'light' : 'dark'));
  }, []);

  const setAccent = useCallback((color: AccentColor) => {
    setAccentState(color);
  }, []);

  return (
    <ThemeContext.Provider value={{ mode, accent, toggleMode, setAccent }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => useContext(ThemeContext);